import { ProductionRouteAsset } from '@/features/production-routes/api/ProductionRouteModel'
import { AssetDefinition } from '@/shared/hooks/api/appConfig'
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  CheckboxGroup,
  InputGroup,
  NumericInputGroup
} from '@/common'
import { useTranslation } from 'react-i18next'
import { TrashIcon } from '@/common/components/icons'
import { ChangeEventHandler, useCallback } from 'react'

export function AssetInstanceListItem({
  asset,
  assetDefinition,
  onRemove,
  onUpdate,
  isElectrolyticCellSystem
}: {
  asset: ProductionRouteAsset
  assetDefinition?: AssetDefinition
  onRemove: () => void
  onUpdate: (asset: ProductionRouteAsset) => void
  isElectrolyticCellSystem?: boolean
}) {
  const { t } = useTranslation()

  const title = assetDefinition ? t(`assets:${assetDefinition.id}`) : asset.customName
  const isCombustion = assetDefinition?.emissionTypes.includes('COMBUSTION') || asset.isCustom // All custom assets are combustion

  const onCustomIdChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    evt => onUpdate({ ...asset, customId: evt.target.value }),
    [onUpdate, asset]
  )

  const onUsesFuelChange = useCallback(
    () => onUpdate({ ...asset, usesFuel: !asset.usesFuel }),
    [onUpdate, asset]
  )

  const onNumberOfAssetsChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    evt => {
      const value = parseInt(evt.target.value)
      onUpdate({ ...asset, quantity: isNaN(value) ? 1 : value })
    },
    [onUpdate, asset]
  )

  return (
    <li key={asset.id}>
      <Card variant="subtle">
        <CardHeader className="flex items-center justify-between">
          <CardTitle title={title} />
          <button onClick={onRemove}>
            <TrashIcon className="size-6" />
          </button>
        </CardHeader>
        <CardBody className="grid grid-cols-12 gap-2 items-end">
          {isElectrolyticCellSystem && (
            <div className="col-span-2">
              <NumericInputGroup
                label={t('report:assetInstanceListItem.fields.numberOfAssets.label')}
                value={asset.quantity.toString()}
                numericFormat={{
                  decimalScale: 0,
                  fixedDecimalScale: true,
                  isAllowed: values => values.floatValue !== undefined && values.floatValue >= 1
                }}
                onChange={onNumberOfAssetsChange}
              />
            </div>
          )}
          <div className="col-span-6">
            <InputGroup
              label={t('report:assetInstanceListItem.fields.customId.label')}
              className="w-full"
              placeholder={t('report:assetInstanceListItem.fields.customId.placeholder')}
              type="text"
              value={asset.customId || ''}
              tooltip={t('report:assetInstanceListItem.fields.customId.tooltip')}
              onChange={onCustomIdChange}
            />
          </div>
          {isElectrolyticCellSystem && (
            <div className="col-span-2 mb-2.5">
              <CheckboxGroup
                id={`${asset.id}-uses-fuel`}
                label={t('report:assetInstanceListItem.fields.usesFuel.label')}
                disabled={isCombustion}
                checked={asset.usesFuel}
                onChange={onUsesFuelChange}
              />
            </div>
          )}
        </CardBody>
      </Card>
    </li>
  )
}
